/**
 * Slug rules — owner directive 2026-02-01. Kept identical to
 * `routers_browse.py::_slugify` so browser-built URLs round-trip to
 * the same public-visibility gate the backend uses.
 *
 *   lowercase, spaces → hyphens, strip non `[a-z0-9-]`, ASCII-fold
 *   accents, collapse and trim hyphens.
 */
export function slugify(input) {
  if (typeof input !== "string" || !input) return "";
  const folded = input.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  const lowered = folded.toLowerCase();
  const replaced = lowered.replace(/[^a-z0-9]+/g, "-");
  return replaced.replace(/-+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Canonical set URL.
 *
 * We prefer the descriptive slug when the set name is available.
 * Callers that only have `external_id` on hand can pass it as
 * `setNameOrExt` — slugify() will just canonicalise whatever it gets,
 * and the set-detail page's own resolver will normalise on landing.
 */
export function setPath(game, lang, setNameOrExt) {
  return `/${game}/${lang}/sets/${slugify(setNameOrExt)}`;
}

/**
 * Canonical card URL.
 *
 * Rules (owner directive 2026-02-01):
 *  - The last segment is the variant token; when the variant is the
 *    card's DEFAULT printing we omit the segment entirely (bare form).
 *  - `defaultVariantSlug` is the slug returned by the backend
 *    (`get_card` payload). Pass it when you know it; when you don't,
 *    we always render the variant segment (safest — the card page
 *    will replace-navigate to the bare form on landing).
 */
export function cardPath({
  game,
  lang,
  setName,
  cardName,
  number,
  variantSlug,
  defaultVariantSlug,
}) {
  // Owner directive 2026-02-01 #3: URL number segment is the stored
  // `localId` lowercased + URL-safe (e.g. "SWSH176" → "swsh176"). The
  // visible page keeps its formatted display via `formatCardNumber`.
  const urlNumber = encodeURIComponent(String(number).toLowerCase());
  const base = `/${game}/${lang}/sets/${slugify(setName)}/${slugify(cardName)}/${urlNumber}`;
  if (!variantSlug) return base;
  if (defaultVariantSlug && variantSlug === defaultVariantSlug) return base;
  return `${base}/${encodeURIComponent(variantSlug)}`;
}

/**
 * Canonical showcase URL — nested child of the card+variant canonical.
 *
 * Owner directive 2026-02-01 (showcase pass):
 *   /pokemon/en/sets/{set}/{card}/{n}[/{variant}]/showcase/{submission_id}
 *
 * The `[variant]` segment is present ONLY when the parent card URL has
 * it (i.e. non-default printing). This matches the card page's
 * bare-when-default rule so share URLs stay clean for the common case.
 * `showcase` is reserved — it can never collide with a variant slug
 * (variants are one of: normal, holo, reverse-holo, first-edition,
 * promo).
 */
export function showcasePath({
  game,
  lang,
  setName,
  cardName,
  number,
  variantSlug,
  defaultVariantSlug,
  submissionId,
}) {
  const cardBase = cardPath({
    game, lang, setName, cardName, number, variantSlug, defaultVariantSlug,
  });
  return `${cardBase}/showcase/${encodeURIComponent(submissionId)}`;
}
